import { insightCategoryBreakdownSchema } from './insight-category-breakdown.js';
const count = (measurement, label, value) => ({
    sourceRef: { measurement },
    label,
    unit: 'count',
    result: { state: 'available', value },
});
export function buildProductMixPresentation(answer) {
    const c = answer.coverage;
    const base = count('observedBuyers', 'Identified buyers in collected paid purchases', c.observedBuyers);
    if (answer.state === 'unavailable')
        base.result = {
            state: 'unavailable',
            reason: 'source_contract_unavailable',
            message: 'Not available',
        };
    const help = {
        appliesTo: [{ measurement: 'observedBuyers' }],
        why: 'Shows the kinds of products bought in the paid purchases collected so far. Use it as assortment context when considering a product pairing or follow-up idea, not a prediction of demand.',
        how: 'Each type counts distinct identified customers with a known paid product of that captured type. Every share uses all identified buyers in qualifying collected baskets, including buyers with missing types. A buyer can appear in several types, so shares need not total 100%. Only non-test, non-cancelled paid or partially refunded orders with positive retained quantity and discounted unit price qualify. Types are not guessed from names or the catalog.',
        notes: [
            {
                code: 'type_coverage',
                text: 'Unknown type evidence is kept separate from known types. A buyer with both is counted once in the shared base.',
                sources: [],
                values: [
                    base,
                    count('knownTypeBuyers', 'Buyers with at least one known type', c.knownTypeBuyers),
                    count('noKnownTypeBuyers', 'Buyers with no known type', c.noKnownTypeBuyers),
                    count('mixedTypeBuyers', 'Buyers with both known and unknown types', c.mixedTypeBuyers),
                    count('anonymousBaskets', 'Qualifying baskets without a customer', c.anonymousBaskets),
                    count('knownTypeLines', 'Qualifying lines with a known type', c.knownTypeLines),
                    count('unknownTypeLines', 'Qualifying lines with an unknown type', c.unknownTypeLines),
                ],
            },
            {
                code: 'paid_coverage',
                text: 'Missing current order lines and unknown eligibility are not measured zero purchases. Present lines do not prove complete collection.',
                sources: [],
                values: [
                    count('totalOrders', 'Collected orders', c.totalOrders),
                    count('qualifyingBaskets', 'Qualifying paid baskets', c.qualifyingBaskets),
                    count('excludedOrders', 'Excluded orders', c.excludedOrders),
                    count('unknownOrders', 'Orders with unknown eligibility', c.unknownOrders),
                    count('eligibleOrdersWithoutLines', 'Eligible orders without current lines', c.eligibleOrdersWithoutLines),
                    count('excludedLines', 'Excluded lines in eligible orders', c.excludedLines),
                    count('unknownLines', 'Lines with unknown eligibility', c.unknownLines),
                    count('unknownDates', 'Qualifying baskets without a date', c.unknownDates),
                ],
            },
            {
                code: 'observed_sources',
                text: 'Collected purchases are not full lifetime history or a random sample. Dates cover qualifying baskets only, including anonymous baskets; update times describe collection, not purchase dates.',
                values: [],
                sources: [
                    {
                        source: 'observedWindow',
                        label: 'Observed paid purchase dates',
                        start: c.observedStart,
                        end: c.observedEnd,
                    },
                    { source: 'orders', label: 'Orders collected', syncedAt: c.ordersSyncedAt },
                    { source: 'order_lines', label: 'Order lines collected', syncedAt: c.linesSyncedAt },
                ],
            },
        ],
    };
    let state = 'ready';
    let message = null;
    let footer = `${c.observedBuyers.toLocaleString('en-US')} identified paid buyers · ${c.noKnownTypeBuyers.toLocaleString('en-US')} with no known type · ${c.mixedTypeBuyers.toLocaleString('en-US')} with known and unknown types · ${c.anonymousBaskets.toLocaleString('en-US')} anonymous baskets. Membership overlaps; collected history may be incomplete.`;
    if (answer.state === 'unavailable') {
        state = 'unavailable';
        footer = `${c.totalOrders.toLocaleString('en-US')} collected orders · ${c.eligibleOrdersWithoutLines.toLocaleString('en-US')} eligible orders without current lines · ${c.unknownOrders.toLocaleString('en-US')} orders and ${c.unknownLines.toLocaleString('en-US')} lines with unknown eligibility. This evidence cannot establish a paid-buyer base.`;
        message =
            answer.reason === 'no_store_data'
                ? 'No collected orders are available yet.'
                : answer.reason === 'source_incomplete'
                    ? 'Eligible orders have no current lines, so paid product types cannot be measured yet.'
                    : 'Order or product eligibility is unknown, so paid product types cannot be measured yet.';
    }
    else if (!answer.categories.length) {
        state = 'empty';
        message =
            c.observedBuyers > 0
                ? 'None of these identified paid buyers has a known product type.'
                : 'No identified buyers with qualifying paid products appear in the collected orders.';
    }
    return insightCategoryBreakdownSchema.parse({
        kind: 'category-breakdown',
        id: 'product-mix',
        title: 'Product types in paid purchases',
        state,
        message,
        base,
        rows: answer.categories.map((x) => ({
            key: x.label,
            label: x.label,
            count: {
                sourceRef: { categoryKey: x.label, field: 'buyers' },
                label: 'Buyers',
                unit: 'count',
                result: { state: 'available', value: x.buyers },
            },
            share: {
                sourceRef: { categoryKey: x.label, field: 'share' },
                label: 'Share of observed paid buyers',
                unit: 'proportion',
                result: { state: 'available', value: x.share },
                baseRef: { measurement: 'observedBuyers' },
            },
        })),
        previewRows: 5,
        detailsLabel: 'See all product types',
        footer,
        help,
    });
}
//# sourceMappingURL=product-mix-presentation.js.map